"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createTextNode, $insertNodes } from "lexical";
import MobileWrapperDialog from "../mobile-wrapper-dialog/MobileWrapperDialog";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import { useEmoji } from "@/contexts/EmojiContext";
import { useCreateDropEmojiPickerLayer } from "./CreateDropEmojiPickerLayerContext";
interface CreateDropEmojiPickerProps {
  disabled?: boolean | undefined;
  top?: string | undefined;
}
interface CreateDropEmojiPickerContentProps {
  disabled: boolean;
  top: string;
}
const CreateDropEmojiPickerContent: FC<CreateDropEmojiPickerContentProps> = t0 => {
  const $ = _c(39);
  const {
    disabled,
    top
  } = t0;
  const isMobile = useIsMobileScreen();
  const {
    desktopZIndex,
    mobileZIndexClassName
  } = useCreateDropEmojiPickerLayer();
  const {
    emojiMap,
    categories,
    categoryIcons
  } = useEmoji();
  const [editor] = useLexicalComposerContext();
  const [showPicker, setShowPicker] = useState(false);
  const buttonRef = useRef(null);
  const pickerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      top: 0,
      left: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [pickerPosition, setPickerPosition] = useState(t1);
  const isPickerOpen = showPicker && !disabled;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const button = buttonRef.current;
      if (!button) {
        return null;
      }
      const rect = button.getBoundingClientRect();
      return {
        top: rect.top + globalThis.scrollY - 420,
        left: rect.left + globalThis.scrollX - 250
      };
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getPickerPosition = t2;
  let t3;
  if ($[2] !== disabled || $[3] !== isPickerOpen) {
    t3 = () => {
      if (disabled) {
        return;
      }
      if (isPickerOpen) {
        setShowPicker(false);
        return;
      }
      const nextPickerPosition = getPickerPosition();
      if (nextPickerPosition) {
        setPickerPosition(nextPickerPosition);
      }
      setShowPicker(true);
    };
    $[2] = disabled;
    $[3] = isPickerOpen;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleTogglePicker = t3;
  let t4;
  if ($[5] !== disabled || $[6] !== editor) {
    t4 = emoji => {
      if (disabled) {
        setShowPicker(false);
        return;
      }
      let emojiText = emoji.native;
      if (!emojiText && emoji.id) {
        emojiText = `:${emoji.id}:`;
      }
      if (emojiText) {
        editor.update(() => {
          const emojiNode = $createTextNode(emojiText);
          $insertNodes([emojiNode]);
        });
        requestAnimationFrame(() => {
          editor.focus();
          editor.update(_temp);
        });
      }
      setShowPicker(false);
    };
    $[5] = disabled;
    $[6] = editor;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const addEmoji = t4;
  let t5;
  let t6;
  if ($[8] !== isPickerOpen) {
    t5 = () => {
      const handleClickOutside = event => {
        if (pickerRef.current && !pickerRef.current.contains(event.target as Node) && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
          setShowPicker(false);
        }
      };
      if (isPickerOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      } else {
        document.removeEventListener("mousedown", handleClickOutside);
      }
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t6 = [isPickerOpen];
    $[8] = isPickerOpen;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  const t7 = `tw-absolute tw-right-2 ${top} tw-flex tw-items-start tw-justify-center`;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <svg className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-transition tw-duration-300 tw-ease-out" xmlns="http://www.w3.org/2000/svg" fill="currentColor" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><g transform="scale(0.024)"><path d="M958 104h-62V43q0-17-12-29T855 2h-87q-17 0-29 12t-12 29v61h-62q-17 0-29 12t-12 29v4q-81-34-169-34-116 0-217 59-97 57-154 154-58 100-58 216.5T84 761q57 98 154 155 101 58 217.5 58T672 916q97-57 154-155 59-100 59-216 0-88-34-168h4q17 0 29-12t12-29v-62h62q17 0 29-12t12-29v-88q0-17-12-29t-29-12zM644 348q29 0 49.5 20.5t20.5 49-20.5 49T644 487t-49.5-20.5-20.5-49 20.5-49T644 348zm-377 0q29 0 49.5 20.5t20.5 49-20.5 49T267 487t-49.5-20.5-20.5-49 20.5-49T267 348zm473 255q-10 70-50.5 126.5t-102 88.5-132 32-132-32-102-88.5T171 603q-2-16 8.5-27.5T206 564h499q16 0 26.5 11.5T740 603zm218-370H855v103h-87V233H665v-88h103V43h87v102h103v88z" /></g></svg>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== disabled || $[13] !== handleTogglePicker) {
    t9 = <button ref={buttonRef} className="tw-flex tw-items-center tw-justify-center tw-rounded tw-border-none tw-bg-transparent tw-p-[0.35rem] tw-opacity-50 tw-transition tw-duration-150 hover:tw-bg-[rgb(40,40,40)] hover:tw-text-[#FFCC22] hover:tw-opacity-100" onClick={handleTogglePicker} disabled={disabled} type="button">{t8}</button>;
    $[12] = disabled;
    $[13] = handleTogglePicker;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== addEmoji || $[16] !== categories || $[17] !== categoryIcons || $[18] !== desktopZIndex || $[19] !== emojiMap || $[20] !== isMobile || $[21] !== isPickerOpen || $[22] !== pickerPosition) {
    t10 = !isMobile && isPickerOpen && createPortal(<div ref={pickerRef} style={{
      position: "absolute",
      top: pickerPosition.top,
      left: pickerPosition.left,
      zIndex: desktopZIndex
    }} className="tw-rounded-lg tw-border tw-bg-iron-800 tw-p-[1px] tw-shadow-lg"><Picker theme="dark" data={data} onEmojiSelect={addEmoji} custom={emojiMap} categories={categories} categoryIcons={categoryIcons} /></div>, document.body);
    $[15] = addEmoji;
    $[16] = categories;
    $[17] = categoryIcons;
    $[18] = desktopZIndex;
    $[19] = emojiMap;
    $[20] = isMobile;
    $[21] = isPickerOpen;
    $[22] = pickerPosition;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t7 || $[26] !== t9) {
    t11 = <div className={t7}>{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t7;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== addEmoji || $[29] !== categories || $[30] !== categoryIcons || $[31] !== emojiMap || $[32] !== isMobile || $[33] !== isPickerOpen || $[34] !== mobileZIndexClassName) {
    t12 = isMobile && <MobileWrapperDialog isOpen={isPickerOpen} onClose={() => setShowPicker(false)} zIndexClassName={mobileZIndexClassName}><div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center" onTouchMove={_temp2}><Picker theme="dark" data={data} onEmojiSelect={addEmoji} custom={emojiMap} categories={categories} categoryIcons={categoryIcons} /></div></MobileWrapperDialog>;
    $[28] = addEmoji;
    $[29] = categories;
    $[30] = categoryIcons;
    $[31] = emojiMap;
    $[32] = isMobile;
    $[33] = isPickerOpen;
    $[34] = mobileZIndexClassName;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
};
const CreateDropEmojiPicker: FC<CreateDropEmojiPickerProps> = t0 => {
  const $ = _c(4);
  const {
    disabled: t1,
    top: t2
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const top = t2 === undefined ? "tw-top-2" : t2;
  const t3 = disabled ? "disabled" : "enabled";
  let t4;
  if ($[0] !== disabled || $[1] !== t3 || $[2] !== top) {
    t4 = <CreateDropEmojiPickerContent key={t3} disabled={disabled} top={top} />;
    $[0] = disabled;
    $[1] = t3;
    $[2] = top;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
};
export default CreateDropEmojiPicker;
function _temp() {}
function _temp2(e) {
  return e.stopPropagation();
}